import { computed, ref } from 'vue'
import type { MemberDiscountRow, MemberDiscountsResponse, MemberDiscountType } from '~/shared/discounts/member.js'

export interface SaveMemberDiscountInput {
  memberId: string
  year: string
  type: MemberDiscountType
  /** Set for 'percent'. */
  percentOff?: number | null
  /** Cents, set for 'fixed'. */
  fixedPrice?: number | null
  reason: string
}

/** Status as admins see it. 'Not needed': unspent, but they already paid another way. */
export function memberDiscountStatusLabel(row: MemberDiscountRow): string {
  if (row.status === 'used') return 'Used'
  if (row.status === 'revoked') return 'Removed'
  if (row.paidWithoutIt) return 'Not needed'
  return 'Available'
}

/**
 * Phosphor tag for a discount's state: a used one did its job (OK), one that
 * will never be used because they already paid deserves a look (gold), and a
 * live or removed one is neutral. Red is kept for errors.
 */
export function memberDiscountTagClass(row: MemberDiscountRow): string {
  if (row.status === 'used') return 'kb-tag-ok'
  if (row.status === 'revoked') return 'kb-tag-neutral'
  if (row.paidWithoutIt) return 'kb-tag-warn'
  return 'kb-tag-neutral'
}

function fetchErrorMessage(err: unknown, fallback: string): string {
  const e = err as { data?: { message?: string, statusMessage?: string }, message?: string } | null
  return e?.data?.message || e?.data?.statusMessage || e?.message || fallback
}

/**
 * Admin access to individual members' membership discounts. List by parade
 * year for the report, or by member for the member drawer.
 */
export function useMemberDiscounts() {
  const response = ref<MemberDiscountsResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const rows = computed(() => response.value?.items ?? [])
  const tiers = computed(() => response.value?.tiers ?? {})
  const totals = computed(() => response.value?.totals ?? null)
  const paradeYear = computed(() => response.value?.paradeYear ?? null)

  async function refresh(params: { year?: string, memberId?: string } = {}) {
    loading.value = true
    error.value = null
    try {
      const query: Record<string, string> = {}
      if (params.year) query.year = params.year
      if (params.memberId) query.memberId = params.memberId
      response.value = await $fetch<MemberDiscountsResponse>('/api/admin/member-discounts', { query })
    }
    catch (err) {
      error.value = fetchErrorMessage(err, 'Failed to load member discounts')
      response.value = null
    }
    finally {
      loading.value = false
    }
  }

  /** Throws with the server's message on failure. */
  async function save(input: SaveMemberDiscountInput): Promise<void> {
    try {
      await $fetch('/api/admin/member-discounts', { method: 'POST', body: input })
    }
    catch (err) {
      throw new Error(fetchErrorMessage(err, 'Failed to save the discount'))
    }
  }

  /** Throws with the server's message on failure. */
  async function revoke(memberId: string, year: string): Promise<void> {
    try {
      await $fetch('/api/admin/member-discounts/revoke', { method: 'POST', body: { memberId, year } })
    }
    catch (err) {
      throw new Error(fetchErrorMessage(err, 'Failed to remove the discount'))
    }
  }

  return { response, rows, tiers, totals, paradeYear, loading, error, refresh, save, revoke }
}
